import ApiService from './api.service';
import type { EllieArtifact } from './ellieArtifact.service';

export type { EllieArtifact };

export type RequestStatus =
  | 'awaiting_pliable'
  | 'awaiting_customer'
  | 'in_progress'
  | 'done'
  | 'declined';

export type RequestType =
  | 'data_source'
  | 'data_quality'
  | 'question'
  | 'change'
  | 'other';

export type MessageAuthorType =
  | 'customer'
  | 'pliable'
  | 'ellie'
  | 'agent'
  | 'system';

export const OPEN_STATUSES: RequestStatus[] = [
  'awaiting_pliable',
  'awaiting_customer',
  'in_progress',
];

export const STATUS_LABEL: Record<RequestStatus, string> = {
  awaiting_pliable: 'Awaiting Pliable',
  awaiting_customer: 'Awaiting you',
  in_progress: 'In progress',
  done: 'Done',
  declined: 'Declined',
};

export const TYPE_LABEL: Record<RequestType, string> = {
  data_source: 'Data source',
  data_quality: 'Data quality',
  question: 'Question',
  change: 'Change',
  other: 'Other',
};

export type SupportRequest = {
  id: string;
  title: string;
  description: string;
  request_type: RequestType;
  status: RequestStatus;
  created_by?: string | null;
  created_by_label?: string | null;
  created_at: string;
  subscriber_ids: string[];
  opened_by_ellie: boolean;
  source_thread_id?: string | null;
  task_ids: string[];
  actionable: boolean;
  actionable_by?: string | null;
  actionable_at?: string | null;
  last_message_at?: string | null;
  last_message_preview: string;
  last_message_author_label?: string | null;
  last_message_author_type?: MessageAuthorType | null;
  message_count: number;
  resolved_at?: string | null;
  resolved_by?: string | null;
  /** Present only on endpoints that compute it for the current user. */
  unread_count?: number;
};

/** A person notified about a request, resolved server-side from an actor id. */
export type RequestSubscriber = {
  user_id: string;
  name: string;
  email: string;
  is_account_rep: boolean;
  is_requester: boolean;
};

export type RequestMessage = {
  id: string;
  request_id: string;
  parent_message_id?: string | null;
  author_type: MessageAuthorType;
  author_id?: string | null;
  author_label?: string | null;
  /** Set when an agent message was triggered by a staff member — that person
   *  owns the message for editing purposes. */
  posted_by_actor_id?: string | null;
  body: string;
  attachment_ids: string[];
  artifact_ids: string[];
  mentions: string[];
  event_type?: string | null;
  created_at: string;
  edited_at?: string | null;
};

export type RequestAttachment = {
  id: string;
  request_id: string;
  message_id?: string | null;
  filename: string;
  mime_type: string;
  size: number;
  storage_key: string;
  uploaded_by?: string | null;
  uploaded_at: string;
  confirmed: boolean;
};

/** A message plus every reply to it. Exactly one level — replies never nest. */
export type RequestThread = {
  root: RequestMessage;
  replies: RequestMessage[];
};

/** One poll feeds both the unread indicators and the sidebar badge. */
export type RequestBadgeSummary = {
  /** requestId -> unread message count for the current user */
  unread: Record<string, number>;
  total: number;
  /** open requests per status, e.g. { awaiting_pliable: 3, ... } */
  status_counts: Record<RequestStatus, number>;
};

export type ListRequestsParams = {
  status?: RequestStatus | 'open' | 'all';
  requestType?: RequestType;
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
};

export type ListRequestsResult = {
  records: SupportRequest[];
  total_records: number;
  total_pages: number;
};

export type CreateRequestPayload = {
  title: string;
  description?: string;
  request_type?: RequestType;
  subscriber_ids?: string[];
};

export class SupportRequestService {
  async list(params?: ListRequestsParams): Promise<ListRequestsResult> {
    const {
      status = 'open',
      requestType,
      search,
      page = 1,
      pageSize = 50,
      sort = '-last_message_at',
    } = params || {};

    const filter: Record<string, unknown> = {};
    if (status === 'open') {
      filter.status = { $in: OPEN_STATUSES };
    } else if (status && status !== 'all') {
      filter.status = status;
    }
    if (requestType) filter.request_type = requestType;
    if (search && search.trim()) {
      filter.title = { $regex: search.trim(), $options: 'i' };
    }

    const query: Record<string, unknown> = { page, limit: pageSize, sort };
    if (Object.keys(filter).length > 0) query.filter = JSON.stringify(filter);

    return (await ApiService.getInstance().request(
      'GET',
      '/requests',
      query,
    )) as ListRequestsResult;
  }

  async get(id: string): Promise<SupportRequest> {
    const result = (await ApiService.getInstance().request(
      'GET',
      `/requests/${id}`,
    )) as { record: SupportRequest };
    return result.record;
  }

  async create(payload: CreateRequestPayload): Promise<SupportRequest> {
    const result = (await ApiService.getInstance().request(
      'POST',
      '/requests',
      payload,
    )) as { record: SupportRequest };
    return result.record;
  }

  async getMessages(id: string): Promise<RequestMessage[]> {
    const result = (await ApiService.getInstance().request(
      'GET',
      `/requests/${id}/messages`,
    )) as { records: RequestMessage[] };
    return result.records ?? [];
  }

  async postMessage(
    id: string,
    body: string,
    opts?: {
      attachmentIds?: string[];
      artifactIds?: string[];
      parentMessageId?: string | null;
      mentions?: string[];
    },
  ): Promise<RequestMessage> {
    const result = (await ApiService.getInstance().request(
      'POST',
      `/requests/${id}/messages`,
      {
        body,
        attachment_ids: opts?.attachmentIds ?? [],
        artifact_ids: opts?.artifactIds ?? [],
        parent_message_id: opts?.parentMessageId ?? null,
        mentions: opts?.mentions ?? [],
      },
    )) as { record: RequestMessage };
    return result.record;
  }

  /**
   * Artifacts attached anywhere on this request, markdown included.
   *
   * Served through the request rather than /artifacts/:id because attaching an
   * artifact to a conversation is what grants access to the people on it — the
   * artifact's own sharing list is not consulted.
   */
  async getArtifacts(id: string): Promise<EllieArtifact[]> {
    const result = (await ApiService.getInstance().request(
      'GET',
      `/requests/${id}/artifacts`,
    )) as { records: EllieArtifact[] };
    return result.records ?? [];
  }

  async getArtifact(id: string, artifactId: string): Promise<EllieArtifact> {
    const result = (await ApiService.getInstance().request(
      'GET',
      `/requests/${id}/artifacts/${artifactId}`,
    )) as { record: EllieArtifact };
    return result.record;
  }

  /**
   * The whole thread a message belongs to.
   *
   * Takes any message id — top-level post or reply — and the backend resolves
   * the root, so callers never have to know which end they are holding.
   */
  async getThread(id: string, messageId: string): Promise<RequestThread> {
    return (await ApiService.getInstance().request(
      'GET',
      `/requests/${id}/messages/${messageId}/thread`,
    )) as RequestThread;
  }

  /** Rename / retype. The API accepts only these fields. */
  async update(
    id: string,
    patch: { title?: string; description?: string; request_type?: RequestType },
  ): Promise<SupportRequest> {
    const result = (await ApiService.getInstance().request(
      'PATCH',
      `/requests/${id}`,
      patch,
    )) as { record: SupportRequest };
    return result.record;
  }

  /** Destructive: removes the thread, its files and its read cursors. */
  async remove(id: string): Promise<void> {
    await ApiService.getInstance().request('DELETE', `/requests/${id}`);
  }

  /**
   * Whether the current user may edit or delete this request.
   *
   * Asked of the server rather than re-derived from roles in the UI, so the
   * button that appears and the rule that is enforced come from one function.
   */
  async permissions(id: string): Promise<{ can_manage: boolean }> {
    return (await ApiService.getInstance().request(
      'GET',
      `/requests/${id}/permissions`,
    )) as { can_manage: boolean };
  }

  /**
   * Who is notified about this request.
   *
   * Names and emails are resolved by the server rather than joined against the
   * tenant user list here, because a subscriber can outlive their directory
   * entry and the list still has to say who the notification goes to.
   */
  async getSubscribers(id: string): Promise<RequestSubscriber[]> {
    const result = (await ApiService.getInstance().request(
      'GET',
      `/requests/${id}/subscribers`,
    )) as { records: RequestSubscriber[] };
    return result.records;
  }

  async addSubscribers(id: string, userIds: string[]): Promise<RequestSubscriber[]> {
    const result = (await ApiService.getInstance().request(
      'POST',
      `/requests/${id}/subscribers`,
      { user_ids: userIds },
    )) as { records: RequestSubscriber[] };
    return result.records;
  }

  async removeSubscriber(id: string, userId: string): Promise<RequestSubscriber[]> {
    const result = (await ApiService.getInstance().request(
      'DELETE',
      `/requests/${id}/subscribers/${userId}`,
    )) as { records: RequestSubscriber[] };
    return result.records;
  }

  async editMessage(
    requestId: string,
    messageId: string,
    body: string,
  ): Promise<RequestMessage> {
    const result = (await ApiService.getInstance().request(
      'PATCH',
      `/requests/${requestId}/messages/${messageId}`,
      { body },
    )) as { record: RequestMessage };
    return result.record;
  }

  async deleteMessage(requestId: string, messageId: string): Promise<void> {
    await ApiService.getInstance().request(
      'DELETE',
      `/requests/${requestId}/messages/${messageId}`,
    );
  }

  /** Remove one file or artifact from a message, leaving the rest in place. */
  async detachFromMessage(
    requestId: string,
    messageId: string,
    what: { attachmentId?: string; artifactId?: string },
  ): Promise<RequestMessage> {
    const result = (await ApiService.getInstance().request(
      'POST',
      `/requests/${requestId}/messages/${messageId}/detach`,
      {
        attachment_id: what.attachmentId ?? null,
        artifact_id: what.artifactId ?? null,
      },
    )) as { record: RequestMessage };
    return result.record;
  }

  async setStatus(
    id: string,
    status: RequestStatus,
    note?: string | null,
  ): Promise<SupportRequest> {
    const result = (await ApiService.getInstance().request(
      'POST',
      `/requests/${id}/status`,
      { status, note: note ?? null },
    )) as { record: SupportRequest };
    return result.record;
  }

  /** Moves the caller's read cursor to now. Cancels any pending email for them. */
  /**
   * Move the read cursor to now, and report where it was beforehand.
   *
   * `previous_read_at` is the whole point of the return value: opening a
   * request marks it read, so it is the only remaining answer to "what hadn't
   * they seen". Null means they had never opened this request.
   */
  async markRead(
    id: string,
  ): Promise<{ previous_read_at: string | null; read_at: string | null }> {
    const result = (await ApiService.getInstance().request(
      'POST',
      `/requests/${id}/read`,
      {},
    )) as {
      previous_read_at: string | null;
      record?: { last_read_at?: string | null };
    };
    return {
      previous_read_at: result?.previous_read_at ?? null,
      // Where the cursor now stands. Used when the reader is watching the
      // thread live: everything up to here has been seen as it arrived.
      read_at: result?.record?.last_read_at ?? null,
    };
  }

  async unreadSummary(): Promise<RequestBadgeSummary> {
    return (await ApiService.getInstance().request(
      'GET',
      '/requests/unread',
    )) as RequestBadgeSummary;
  }

  async search(q: string): Promise<SupportRequest[]> {
    const result = (await ApiService.getInstance().request('GET', '/requests/search', {
      q,
    })) as { records: SupportRequest[] };
    return result.records ?? [];
  }

  async getAttachments(id: string): Promise<RequestAttachment[]> {
    const result = (await ApiService.getInstance().request(
      'GET',
      `/requests/${id}/attachments`,
    )) as { records: RequestAttachment[] };
    return result.records ?? [];
  }

  async downloadUrl(requestId: string, attachmentId: string): Promise<string> {
    const result = (await ApiService.getInstance().request(
      'GET',
      `/requests/${requestId}/attachments/${attachmentId}/download`,
    )) as { url: string };
    return result.url;
  }

  /**
   * Register the file, PUT the bytes straight to S3, then confirm.
   *
   * The row stays unconfirmed — and therefore invisible to the thread and to
   * Jimi — until the PUT lands, so an abandoned upload can't leave a broken
   * attachment on a message.
   */
  async uploadAttachment(
    requestId: string,
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<RequestAttachment> {
    const init = (await ApiService.getInstance().request(
      'POST',
      `/requests/${requestId}/attachments`,
      {
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        size: file.size,
      },
    )) as { record: RequestAttachment; upload_url: string };

    await putWithProgress(init.upload_url, file, onProgress);

    const confirmed = (await ApiService.getInstance().request(
      'POST',
      `/requests/${requestId}/attachments/${init.record.id}/confirm`,
      { size: file.size },
    )) as { record: RequestAttachment };
    return confirmed.record;
  }
}

/** XHR rather than fetch, because fetch gives no upload progress events. */
function putWithProgress(
  url: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}

export const supportRequestService = new SupportRequestService();
